import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

interface FeatureIntroLayoutProps {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  children: ReactNode;
  contactEmail?: string;
}

/**
 * Full-page layout for feature introduction pages.
 * Clean Apple-style design - minimal, spacious, no gradients.
 * Shown to unprovisioned users when they click on ERP modules.
 */
export function FeatureIntroLayout({
  icon: Icon,
  title,
  subtitle,
  children,
  contactEmail = 'support@zaviago.com',
}: FeatureIntroLayoutProps) {
  return (
    <div className="min-h-full flex flex-col items-center px-6 py-16">
      <div className="max-w-xl w-full">
        {/* Icon */}
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <Icon className="w-8 h-8 text-gray-600 dark:text-gray-400" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-semibold text-center text-gray-900 dark:text-gray-100 mb-3">
          {title}
        </h1>

        {/* Subtitle */}
        <p className="text-base text-center text-gray-500 dark:text-gray-400 mb-12 leading-relaxed">
          {subtitle}
        </p>

        {/* Benefits */}
        <div className="space-y-4 mb-12">
          {children}
        </div>

        {/* Contact */}
        <div className="text-center pt-8 border-t border-gray-100 dark:border-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Ready to get started? Contact us at{' '}
            <a
              href={`mailto:${contactEmail}`}
              className="text-gray-900 dark:text-gray-100 hover:underline"
            >
              {contactEmail}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
